import { Toast } from '@modusign/design-system';
import XLSX from '@sheet/edit';
import { ReactNode, useCallback, useEffect, useState } from 'react';

import useCreateBulkContacts from '@app/features/addressBook/hooks/useCreateBulkContacts';
import { numberToColumnName } from '@app/libs/excel';
import {
  AddressBookExcelFormColumnType,
  addressExcelForm,
  createFormValues,
  EXCEL_FORM_ERROR_MESSAGE,
  getColumnName,
  validateFormValues,
} from '@app/libs/excel/addressBook';
import getExtensionFromFileName from '@app/libs/file/getExtensionFromFileName';
import { ContactForBulkCreate } from '@app/store/transport-layer/document/address-book/bulk/contacts';

export const ACCEPT_FILETYPE = ['.xlsx'];

function findFormIndex(type: AddressBookExcelFormColumnType): number {
  return addressExcelForm.findIndex((t) => t === type);
}

interface UseExcelUploadOptions {
  onSuccess?: () => void;
}

export default function useExcelUpload(options?: UseExcelUploadOptions) {
  const [excelValidationErrors, setExcelValidationErrors] = useState<
    Array<{ cell: string; message: ReactNode; columnName: string }>
  >([]);
  const [uploadError, setUploadError] = useState<Error | null>(null);
  const [isExcelLoading, setIsExcelLoading] = useState(false);
  const { mutate: uploadContacts, reset: resetUploadContacts } = useCreateBulkContacts({
    onError: (error) => {
      setUploadError(error);
    },
    onSettled: () => {
      setIsExcelLoading(false);
    },
    onSuccess: (data, variables) => {
      Toast.success({ description: `${variables.length}개의 연락처를 추가했습니다.` });
      options?.onSuccess?.();
    },
  });

  const reset = useCallback(() => {
    setExcelValidationErrors([]);
    setIsExcelLoading(false);
    setUploadError(null);
    resetUploadContacts();
  }, [resetUploadContacts]);

  const uploadExcel = useCallback(
    (file?: File | null) => {
      setIsExcelLoading(true);
      if (!file) {
        setUploadError(new Error(EXCEL_FORM_ERROR_MESSAGE));
        return;
      }

      const isAcceptableFileExtension = ACCEPT_FILETYPE.includes(
        getExtensionFromFileName(file.name)
      );

      if (!isAcceptableFileExtension) {
        setUploadError(new Error(EXCEL_FORM_ERROR_MESSAGE));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        if (e && e.target) {
          try {
            const { result } = e.target;
            const data = new Uint8Array(result as ArrayBuffer);
            const workBook = XLSX.read(data, { type: 'array' });

            const formValues = createFormValues(workBook);
            const excelValidationErrors = validateFormValues(formValues);

            if (excelValidationErrors.length > 0) {
              setExcelValidationErrors(
                excelValidationErrors.map(({ column, row, message, type }) => ({
                  cell: `${numberToColumnName(column)}-${row}`,
                  columnName: getColumnName(type),
                  message,
                }))
              );
            } else {
              uploadContacts(
                formValues.map((contact): ContactForBulkCreate => {
                  return {
                    bizNumber: contact[findFormIndex('BIZ_NUMBER')],
                    companyName: contact[findFormIndex('COMPANY_NAME')],
                    defaultForwardingMethod:
                      /**
                       * 정제된 데이터는 'EMAIL' | 'KAKAO' 인데 string 타입으로 추론되어
                       * 명확하게 타입을 맞추기 위해 ? 연산자로 값을 매핑합니다.
                       */
                      contact[findFormIndex('DEFAULT_FORWARDING_METHOD')] === 'EMAIL'
                        ? 'EMAIL'
                        : 'KAKAO',
                    departmentPosition: contact[findFormIndex('COMPANY_POSITION')],
                    description: contact[findFormIndex('DESCRIPTION')],
                    email: contact[findFormIndex('EMAIL')],
                    name: contact[findFormIndex('NAME')],
                    phoneNumber: contact[findFormIndex('PHONE_NUMBER')],
                  };
                })
              );
            }
          } catch (error) {
            setUploadError(error.message);
          }
        }
      };

      reader.onerror = () => {
        setUploadError(new Error(EXCEL_FORM_ERROR_MESSAGE));
      };

      reader.readAsArrayBuffer(file);
    },
    [uploadContacts]
  );

  useEffect(() => {
    if (uploadError || excelValidationErrors.length > 0) {
      setIsExcelLoading(false);
    }
  }, [excelValidationErrors, uploadError]);

  return {
    excelValidationErrors,
    isExcelLoading,
    reset,
    uploadError,
    uploadExcel,
  };
}
